import { useState, useEffect, useRef } from 'react';
import { useChainId, useSignMessage } from 'wagmi';
import { mainnet } from 'wagmi/chains';
import { toast } from "react-toastify";
import useWallet from '../../../../hooks/useWallet';
import { ApiUrl } from '../../../../utils/ApiUrl';
import { useChainSwitch } from '../../../../hooks/useChainSwitch';
import { isUserRejectionError } from '../../../../utils/constant';

const REQUIRED_CHAIN_ID = mainnet.id;

export const useWalletAuth = () => {
    const { address, isConnected, connect, connectors, disconnect, connector } = useWallet();
    const [isLoginInProgress, setIsLoginInProgress] = useState(false);
    const [isSigningInProgress, setIsSigningInProgress] = useState(false);
    const [shouldInitiateLoginAfterConnect, setShouldInitiateLoginAfterConnect] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [logedIn, setLogedIn] = useState(true);
    const [isAutoSwitching, setIsAutoSwitching] = useState(false);
    const [isSafeWallet, setIsSafeWallet] = useState(false);
    const [signedData, setSignedData] = useState(null);

    const previousAddressRef = useRef(null);
    const lastProcessedAddressRef = useRef(null);
    const lastProcessedSignatureRef = useRef(null);

    const chainId = useChainId();
    const { switchToChain } = useChainSwitch();
    const { signMessageAsync, isPending: isSignPending } = useSignMessage();

    // Helper function to clean up login state
    const cleanupLoginState = () => {
        setIsLoginInProgress(false);
        setIsSigningInProgress(false);
        setShouldInitiateLoginAfterConnect(false);
        lastProcessedAddressRef.current = null;
        lastProcessedSignatureRef.current = null;
        setSignedData(null);
    };

    // Detect wallet type (Safe or other)
    const detectWalletType = async () => {
        try {
            if (!connector) return;
            const provider = await connector?.getProvider();
            // console.log("Provider object:", provider);
            // console.log("Wallet name:", provider?.signer?.session?.peer?.metadata?.name);

            const walletName = provider?.signer?.session?.peer?.metadata?.name;
            const isSafe = walletName === 'Safe{Wallet}';
            // console.log("Is Safe Wallet:", isSafe);
            setIsSafeWallet(isSafe);
        } catch (error) {
            console.error("Error detecting wallet type:", error);
            setIsSafeWallet(false);
        }
    };

    const isUserLoggedIn = () => {
        return !!(localStorage.getItem('accessToken') || localStorage.getItem('refreshToken'));
    };

    useEffect(() => {
        const checkMobile = /Mobi|Android/i.test(navigator.userAgent);
        setIsMobile(checkMobile);
    }, []);

    // Detect wallet type when connector changes
    useEffect(() => {
        if (isConnected && address && connector) {
            detectWalletType();
        }
    }, [isConnected, address, connector]);

    // Detect wallet account switch
    useEffect(() => {
        if (address && previousAddressRef.current) {
            if (previousAddressRef.current.toLowerCase() !== address.toLowerCase()) {
                if (isUserLoggedIn()) {
                    toast.info("Wallet account changed. Please login again.");
                    handleWalletDisconnect();
                }
                cleanupLoginState();
            }
        }

        if (address) {
            previousAddressRef.current = address;
        }
    }, [address]);

    // Auto-switch chain if user is on wrong network
    useEffect(() => {
        const autoSwitchChain = async () => {
            if (isConnected && address && chainId !== REQUIRED_CHAIN_ID && !isAutoSwitching) {
                setIsAutoSwitching(true);
                try {
                    await switchToChain(REQUIRED_CHAIN_ID);
                    setIsAutoSwitching(false);
                } catch (error) {
                    console.error("Auto chain switch error:", error);
                    setIsAutoSwitching(false);
                    cleanupLoginState();
                    disconnect();
                }
            }
        };

        autoSwitchChain();
    }, [isConnected, address, chainId, isAutoSwitching]);

    // Watch for wallet connection and correct chain
    useEffect(() => {
        if (isConnected && address && shouldInitiateLoginAfterConnect && chainId === REQUIRED_CHAIN_ID && !isAutoSwitching) {
            setShouldInitiateLoginAfterConnect(false);
            initiateLoginProcess();
        }
    }, [isConnected, address, shouldInitiateLoginAfterConnect, chainId, isAutoSwitching]);

    // Watch for signed data to call API (v2 flow)
    useEffect(() => {
        if (signedData && signedData.address && signedData.signature && !isSafeWallet) {
            handleLoginAPI(signedData.address, signedData.signature);
            setSignedData(null);
        }
    }, [signedData, isSafeWallet]);

    const handleWalletConnect = async (connector) => {
        try {
            toast.dismiss();
            cleanupLoginState();
            setShouldInitiateLoginAfterConnect(true);
            await connect({ connector });
        } catch (error) {
            toast.error("Failed to connect wallet");
            cleanupLoginState();
        }
    };

    const initiateLoginProcess = async () => {
        try {
            if (!address) {
                throw new Error("Wallet address not available");
            }

            if (!isConnected) {
                throw new Error("Wallet not connected");
            }

            if (chainId !== REQUIRED_CHAIN_ID) {
                throw new Error("Please switch to Ethereum network");
            }

            setIsLoginInProgress(true);
            setIsSigningInProgress(true);
            // Detect wallet type in real-time
            let walletType = isSafeWallet;
            try {
                if (!walletType && connector) {
                    const provider = await connector?.getProvider();
                    const walletName = provider?.signer?.session?.peer?.metadata?.name;
                    walletType = walletName === 'Safe{Wallet}';
                    // console.log("Real-time wallet detection - isSafe:", walletType);
                }
            } catch (e) {
                console.error("Error in real-time wallet detection:", e);
            }

            // Route based on wallet type
            if (walletType) {
                // v3 flow: Safe wallet - direct API call with address
                // console.log("Using v3 flow (Safe Wallet)");
                await handleLoginAPI(address);
            } else {
                // v2 flow: Other wallets - signature-based
                // console.log("Using v2 flow (Sign Message)");
                await initiateSigningProcess();
            }

        } catch (error) {
            toast.error(error.message || "Failed to start login process");
            cleanupLoginState();
            disconnect();
            console.error("initiateLoginProcess error", error);
        }
    };

    const initiateSigningProcess = async () => {
        try {
            if (!address) {
                throw new Error("Wallet address not available");
            }

            const message = `${address.toLowerCase()}weareUniform`;
            const signature = await signMessageAsync({ message });

            if (lastProcessedSignatureRef.current !== signature) {
                lastProcessedSignatureRef.current = signature;
                setSignedData({ address, signature });
            }

        } catch (error) {
            // Determine if this is a user rejection or a different error
            const rejectionMessage = 'User rejected the sign transaction';
            const displayMessage = isUserRejectionError(error)
                ? rejectionMessage
                : error.message || 'Failed to sign message';

            toast.error(displayMessage);
            cleanupLoginState();
            disconnect();
            console.error("initiateSigningProcess error", error);
        }
    };


    const handleLoginAPI = async (walletAddress, signature = null) => {
        try {
            if (!walletAddress) {
                throw new Error("Missing wallet address");
            }

            // Only call API if address/signature hasn't been processed yet
            const processKey = signature ? signature : walletAddress.toLowerCase();
            if (lastProcessedAddressRef.current === processKey) {
                return;
            }

            lastProcessedAddressRef.current = processKey;

            // Determine if Safe wallet based on signature presence
            const isSafe = !signature;

            let payload;
            let endpoint;

            if (isSafe) {
                // v3 flow: Safe wallet (no signature)
                // console.log("Calling Safe endpoint:", `${ApiUrl}/auth/users/signup-signinn`);
                payload = {
                    walletAddress: walletAddress.toLowerCase()
                };
                endpoint = `${ApiUrl}/auth/users/signup-signinn`;
            } else {
                // v2 flow: Other wallets (with signature)
                // console.log("Calling signature endpoint:", `${ApiUrl}/auth/users/signup-signin`);
                payload = {
                    walletAddress: walletAddress.toLowerCase(),
                    sign: signature
                };
                endpoint = `${ApiUrl}/auth/users/signup-signin`;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `API Error: ${response.status}`);
            }

            setLogedIn(data.data);

            if (data.data?.accessToken) {
                localStorage.setItem('accessToken', data.data.accessToken);
            }
            if (data.data?.refreshToken) {
                localStorage.setItem('refreshToken', data.data.refreshToken);
            }

            cleanupLoginState();
            toast.dismiss();

        } catch (error) {
            toast.error(error.message || 'Authentication failed');
            cleanupLoginState();
            disconnect();
            console.error("handleLoginAPI error", error);
        }
    };

    const handleWalletDisconnect = () => {
        cleanupLoginState();
        disconnect();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        previousAddressRef.current = null;
        setIsSafeWallet(false);
        toast.dismiss();
    };
    // console.log("useWalletAuth returns", {isConnected,
    //     isLoginInProgress,
    //     isSigningInProgress,
    //     isMobile,
    //     connectors,
    //     logedIn,
    //     chainId,
    //     isAutoSwitching,
    //     isSafeWallet});
    return {
        isConnected,
        isLoginInProgress,
        isSignPending,
        isSigningInProgress,
        isMobile,
        connectors,
        logedIn,
        chainId,
        isAutoSwitching,
        isSafeWallet,
        handleWalletConnect,
        handleWalletDisconnect,
        switchToChain
    };
};
// //v3
// import { useState, useEffect, useRef } from 'react';
// import { useChainId } from 'wagmi';
// import { mainnet } from 'wagmi/chains';
// import { toast } from "react-toastify";
// import useWallet from '../../../../hooks/useWallet';
// import { ApiUrl } from '../../../../utils/ApiUrl';
// import { useChainSwitch } from '../../../../hooks/useChainSwitch';

// const REQUIRED_CHAIN_ID = mainnet.id;

// export const useWalletAuth = () => {
//     const { address, isConnected, connect, connectors, disconnect } = useWallet();
//     console.log("useWalletAuth address,isConnected", address, isConnected, connectors);
//     const [isLoginInProgress, setIsLoginInProgress] = useState(false);
//     const [shouldInitiateLoginAfterConnect, setShouldInitiateLoginAfterConnect] = useState(false);
//     const [isMobile, setIsMobile] = useState(false);
//     const [logedIn, setLogedIn] = useState(true);
//     const [isAutoSwitching, setIsAutoSwitching] = useState(false);

//     const previousAddressRef = useRef(null);
//     const lastProcessedAddressRef = useRef(null);

//     const chainId = useChainId();
//     const { switchToChain } = useChainSwitch();

//     // Helper function to clean up login state
//     const cleanupLoginState = () => {
//         setIsLoginInProgress(false);
//         setShouldInitiateLoginAfterConnect(false);
//         lastProcessedAddressRef.current = null;
//     };

//     const isUserLoggedIn = () => {
//         return !!(localStorage.getItem('accessToken') || localStorage.getItem('refreshToken'));
//     };

//     useEffect(() => {
//         const checkMobile = /Mobi|Android/i.test(navigator.userAgent);
//         setIsMobile(checkMobile);
//     }, []);

//     // Detect wallet account switch
//     useEffect(() => {
//         if (address && previousAddressRef.current) {
//             if (previousAddressRef.current.toLowerCase() !== address.toLowerCase()) {
//                 if (isUserLoggedIn()) {
//                     toast.info("Wallet account changed. Please login again.");
//                     handleWalletDisconnect();
//                 }
//                 cleanupLoginState();
//             }
//         }

//         if (address) {
//             previousAddressRef.current = address;
//         }
//     }, [address]);

//     // Auto-switch chain if user is on wrong network
//     useEffect(() => {
//         const autoSwitchChain = async () => {
//             if (isConnected && address && chainId !== REQUIRED_CHAIN_ID && !isAutoSwitching) {
//                 setIsAutoSwitching(true);
//                 try {
//                     await switchToChain(REQUIRED_CHAIN_ID);
//                     setIsAutoSwitching(false);
//                 } catch (error) {
//                     console.error("Auto chain switch error:", error);
//                     setIsAutoSwitching(false);
//                     cleanupLoginState();
//                     disconnect();
//                 }
//             }
//         };

//         autoSwitchChain();
//     }, [isConnected, address, chainId, isAutoSwitching]);

//     // Watch for wallet connection and correct chain
//     useEffect(() => {
//         if (isConnected && address && shouldInitiateLoginAfterConnect && chainId === REQUIRED_CHAIN_ID && !isAutoSwitching) {
//             setShouldInitiateLoginAfterConnect(false);
//             initiateLoginProcess();
//         }
//     }, [isConnected, address, shouldInitiateLoginAfterConnect, chainId, isAutoSwitching]);

//     const handleWalletConnect = async (connector) => {
//         try {
//             toast.dismiss();
//             cleanupLoginState();
//             setShouldInitiateLoginAfterConnect(true);
//             await connect({ connector });
//         } catch (error) {
//             toast.error("Failed to connect wallet");
//             cleanupLoginState();
//         }
//     };

//     const initiateLoginProcess = async () => {
//         try {
//             if (!address) {
//                 throw new Error("Wallet address not available");
//             }

//             if (!isConnected) {
//                 throw new Error("Wallet not connected");
//             }

//             if (chainId !== REQUIRED_CHAIN_ID) {
//                 throw new Error("Please switch to Ethereum network");
//             }

//             setIsLoginInProgress(true);

//             // Call API directly with wallet address
//             await handleLoginAPI(address);

//         } catch (error) {
//             toast.error(error.message || "Failed to start login process");
//             cleanupLoginState();
//             disconnect();
//             console.log("initiateLoginProcess error", error);
//         }
//     };

//     const handleLoginAPI = async (walletAddress) => {
//         try {
//             if (!walletAddress) {
//                 throw new Error("Missing wallet address");
//             }

//             // Only call API if address hasn't been processed yet
//             if (lastProcessedAddressRef.current === walletAddress.toLowerCase()) {
//                 return;
//             }

//             lastProcessedAddressRef.current = walletAddress.toLowerCase();

//             const payload = {
//                 walletAddress: walletAddress.toLowerCase()
//             };

//             const response = await fetch(`${ApiUrl}/auth/users/signup-signinn`, {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify(payload)
//             });

//             const data = await response.json();

//             if (!response.ok) {
//                 throw new Error(data.message || `API Error: ${response.status}`);
//             }

//             setLogedIn(data.data);

//             if (data.data?.accessToken) {
//                 localStorage.setItem('accessToken', data.data.accessToken);
//             }
//             if (data.data?.refreshToken) {
//                 localStorage.setItem('refreshToken', data.data.refreshToken);
//             }

//             cleanupLoginState();
//             toast.dismiss();

//         } catch (error) {
//             toast.error(error.message || 'Authentication failed');
//             cleanupLoginState();
//             disconnect();
//         }
//     };

//     const handleWalletDisconnect = () => {
//         cleanupLoginState();
//         disconnect();
//         localStorage.removeItem('accessToken');
//         localStorage.removeItem('refreshToken');
//         previousAddressRef.current = null;
//         toast.dismiss();
//     };

//     return {
//         isConnected,
//         isLoginInProgress,
//         isMobile,
//         connectors,
//         logedIn,
//         chainId,
//         isAutoSwitching,
//         handleWalletConnect,
//         handleWalletDisconnect,
//         switchToChain
//     };
// };
// v2
// import { useState, useEffect, useRef } from 'react';
// import { useSignMessage, useChainId } from 'wagmi';
// import { mainnet } from 'wagmi/chains';
// import { toast } from "react-toastify";
// import useWallet from '../../../../hooks/useWallet';
// import { ApiUrl } from '../../../../utils/ApiUrl';
// import { useChainSwitch } from '../../../../hooks/useChainSwitch';

// const REQUIRED_CHAIN_ID = mainnet.id;

// export const useWalletAuth = () => {
//     const { address, isConnected, connect, connectors, disconnect } = useWallet();
//     const [isSigningInProgress, setIsSigningInProgress] = useState(false);
//     const [shouldInitiateSigningAfterConnect, setShouldInitiateSigningAfterConnect] = useState(false);
//     const [signedData, setSignedData] = useState(null);
//     const [isMobile, setIsMobile] = useState(false);
//     const [logedIn, setLogedIn] = useState(true);
//     const [isAutoSwitching, setIsAutoSwitching] = useState(false);

//     const previousAddressRef = useRef(null);
//     const lastProcessedSignatureRef = useRef(null);

//     const chainId = useChainId();
//     const { switchToChain } = useChainSwitch();

//     const { signMessageAsync,data, error  } = useSignMessage();
// console.log("signMessageAsync data error",data,error);
//     // Helper function to clean up signing state
//     const cleanupSigningState = () => {
//         setIsSigningInProgress(false);
//         setShouldInitiateSigningAfterConnect(false);
//         setSignedData(null);
//         lastProcessedSignatureRef.current = null;
//     };

//     const isUserLoggedIn = () => {
//         return !!(localStorage.getItem('accessToken') || localStorage.getItem('refreshToken'));
//     };

//     useEffect(() => {
//         const checkMobile = /Mobi|Android/i.test(navigator.userAgent);
//         setIsMobile(checkMobile);
//     }, []);

//     // Detect wallet account switch
//     useEffect(() => {
//         if (address && previousAddressRef.current) {
//             if (previousAddressRef.current.toLowerCase() !== address.toLowerCase()) {
//                 if (isUserLoggedIn()) {
//                     toast.info("Wallet account changed. Please login again.");
//                     handleWalletDisconnect();
//                 }
//                 cleanupSigningState();
//             }
//         }

//         if (address) {
//             previousAddressRef.current = address;
//         }
//     }, [address]);

//     // Auto-switch chain if user is on wrong network
//     useEffect(() => {
//         const autoSwitchChain = async () => {
//             if (isConnected && address && chainId !== REQUIRED_CHAIN_ID && !isAutoSwitching) {
//                 setIsAutoSwitching(true);
//                 try {
//                     await switchToChain(REQUIRED_CHAIN_ID);
//                     setIsAutoSwitching(false);
//                 } catch (error) {
//                     console.error("Auto chain switch error:", error);
//                     setIsAutoSwitching(false);
//                     cleanupSigningState();
//                     disconnect();
//                 }
//             }
//         };

//         autoSwitchChain();
//     }, [isConnected, address, chainId, isAutoSwitching]);

//     // Watch for wallet connection and correct chain
//     useEffect(() => {
//         if (isConnected && address && shouldInitiateSigningAfterConnect && chainId === REQUIRED_CHAIN_ID && !isAutoSwitching) {
//             setShouldInitiateSigningAfterConnect(false);
//             initiateSigningProcess();
//         }
//     }, [isConnected, address, shouldInitiateSigningAfterConnect, chainId, isAutoSwitching]);

//     // Watch for signed data to call API
//     useEffect(() => {
//         if (signedData && signedData.address && signedData.signature) {
//             handleLoginAPI(signedData.address, signedData.signature);
//             setSignedData(null);
//         }
//     }, [signedData]);

//     // Timeout for signing process
//     // useEffect(() => {
//     //     let timeout;
//     //     if (isSigningInProgress) {
//     //         timeout = setTimeout(() => {
//     //             toast.error("Signing process timed out. Please try again.");
//     //             cleanupSigningState();
//     //             disconnect();
//     //         }, 60000);
//     //     }

//     //     return () => {
//     //         if (timeout) {
//     //             clearTimeout(timeout);
//     //         }
//     //     };
//     // }, [isSigningInProgress]);

//     const handleWalletConnect = async (connector) => {
//         try {
//             toast.dismiss();
//             cleanupSigningState();
//             setShouldInitiateSigningAfterConnect(true);
//             await connect({ connector });
//         } catch (error) {
//             toast.error("Failed to connect wallet");
//             cleanupSigningState();
//         }
//     };

//     const initiateSigningProcess = async () => {
//         try {
//             if (!address) {
//                 throw new Error("Wallet address not available");
//             }

//             if (!isConnected) {
//                 throw new Error("Wallet not connected");
//             }

//             if (chainId !== REQUIRED_CHAIN_ID) {
//                 throw new Error("Please switch to Etherium network");
//             }

//             const message = `${address?.toLocaleLowerCase()}weareUniform`;
//             setIsSigningInProgress(true);

//             const signature = await signMessageAsync({ message });
//             console.log("initiateSigningProcess signature",signature);
//             if (lastProcessedSignatureRef.current !== signature) {
//                 lastProcessedSignatureRef.current = signature;
//                 setSignedData({ address, signature });
//             }

//         } catch (error) {
//             toast.error(error.message || "Failed to start signing process");
//             cleanupSigningState();
//             disconnect();
//             console.log("initiateSigningProcess error",error);
//         }
//     };

//     const handleLoginAPI = async (walletAddress, signature) => {
//         try {
//             if (!walletAddress || !signature) {
//                 throw new Error("Missing required authentication data");
//             }

//             const payload = {
//                 walletAddress: walletAddress?.toLocaleLowerCase(),
//                 sign: signature
//             };

//             const response = await fetch(`${ApiUrl}/auth/users/signup-signin`, {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify(payload)
//             });

//             const data = await response.json();

//             if (!response.ok) {
//                 throw new Error(data.message || `API Error: ${response.status}`);
//             }

//             setLogedIn(data.data);

//             if (data.data?.accessToken) {
//                 localStorage.setItem('accessToken', data.data.accessToken);
//             }
//             if (data.data?.refreshToken) {
//                 localStorage.setItem('refreshToken', data.data.refreshToken);
//             }

//             cleanupSigningState();
//             toast.dismiss();

//         } catch (error) {
//             toast.error(error.message || 'Authentication failed');
//             cleanupSigningState();
//             disconnect();
//         }
//     };

//     const handleWalletDisconnect = () => {
//         cleanupSigningState();
//         disconnect();
//         localStorage.removeItem('accessToken');
//         localStorage.removeItem('refreshToken');
//         previousAddressRef.current = null;
//         toast.dismiss();
//     };

//     return {
//         isConnected,
//         isSigningInProgress,
//         isMobile,
//         connectors,
//         logedIn,
//         chainId,
//         isAutoSwitching,
//         handleWalletConnect,
//         handleWalletDisconnect,
//         switchToChain
//     };
// };

//v1
// import { useState, useEffect, useRef } from 'react';
// import { useSignMessage, useChainId } from 'wagmi';
// import { mainnet } from 'wagmi/chains';
// import { toast } from "react-toastify";
// import useWallet from '../../../../hooks/useWallet';
// import { ApiUrl } from '../../../../utils/ApiUrl';
// import { useChainSwitch } from '../../../../hooks/useChainSwitch';

// const REQUIRED_CHAIN_ID = mainnet.id;

// export const useWalletAuth = () => {
//     const { address, isConnected, connect, connectors, disconnect } = useWallet();
//     const [isSigningInProgress, setIsSigningInProgress] = useState(false);
//     const [shouldInitiateSigningAfterConnect, setShouldInitiateSigningAfterConnect] = useState(false);
//     const [signedData, setSignedData] = useState(null);
//     const [isMobile, setIsMobile] = useState(false);
//     const [logedIn, setLogedIn] = useState(true);
//     const [isAutoSwitching, setIsAutoSwitching] = useState(false);

//     const previousAddressRef = useRef(null);
//     const lastProcessedSignatureRef = useRef(null); // Track last processed signature

//     const chainId = useChainId();
//     const { switchToChain } = useChainSwitch();

//     const { signMessage, isPending: isSignPending, error: signError, data: signatureData, reset: resetSignature } = useSignMessage();

//     // Helper function to clean up signing state
//     const cleanupSigningState = () => {
//         setIsSigningInProgress(false);
//         setShouldInitiateSigningAfterConnect(false);
//         setSignedData(null);
//         lastProcessedSignatureRef.current = null; // Reset processed signature tracker

//         // Reset wagmi's signature data
//         if (resetSignature) {
//             resetSignature();
//         }
//     };

//     const isUserLoggedIn = () => {
//         return !!(localStorage.getItem('accessToken') || localStorage.getItem('refreshToken'));
//     };

//     useEffect(() => {
//         const checkMobile = /Mobi|Android/i.test(navigator.userAgent);
//         setIsMobile(checkMobile);
//     }, []);

//     // Detect wallet account switch
//     useEffect(() => {
//         if (address && previousAddressRef.current) {
//             if (previousAddressRef.current.toLowerCase() !== address.toLowerCase()) {
//                 if (isUserLoggedIn()) {
//                     toast.info("Wallet account changed. Please login again.");
//                     handleWalletDisconnect();
//                 }
//                 // Clean up stale signature data when address changes
//                 cleanupSigningState();
//             }
//         }

//         if (address) {
//             previousAddressRef.current = address;
//         }
//     }, [address]);

//     // Watch for signing error
//     useEffect(() => {
//         if (signError && isSigningInProgress) {
//             cleanupSigningState();
//             disconnect();
//         }
//     }, [signError, isSigningInProgress]);

//     // Watch for signature data - with stale data prevention
//     useEffect(() => {
//         if (signatureData && !isSignPending && !signError && isSigningInProgress) {
//             // Only process if we haven't processed this signature before and we're actively signing
//             if (lastProcessedSignatureRef.current !== signatureData) {
//                 lastProcessedSignatureRef.current = signatureData;
//                 setSignedData({ address, signature: signatureData });
//             }
//         }
//     }, [signatureData, isSignPending, signError, address, isSigningInProgress]);

//     // Auto-switch chain if user is on wrong network
//     useEffect(() => {
//         const autoSwitchChain = async () => {
//             if (isConnected && address && chainId !== REQUIRED_CHAIN_ID && !isAutoSwitching) {
//                 setIsAutoSwitching(true);
//                 try {
//                     await switchToChain(REQUIRED_CHAIN_ID);
//                     setIsAutoSwitching(false);
//                 } catch (error) {
//                     console.error("Auto chain switch error:", error);
//                     setIsAutoSwitching(false);
//                     cleanupSigningState();
//                     disconnect();
//                 }
//             }
//         };

//         autoSwitchChain();
//     }, [isConnected, address, chainId, isAutoSwitching]);

//     // Watch for wallet connection and correct chain
//     useEffect(() => {
//         if (isConnected && address && shouldInitiateSigningAfterConnect && chainId === REQUIRED_CHAIN_ID && !isAutoSwitching) {
//             setShouldInitiateSigningAfterConnect(false);
//             initiateSigningProcess();
//         }
//     }, [isConnected, address, shouldInitiateSigningAfterConnect, chainId, isAutoSwitching]);

//     // Watch for signed data to call API
//     useEffect(() => {
//         if (signedData && signedData.address && signedData.signature) {
//             handleLoginAPI(signedData.address, signedData.signature);
//             setSignedData(null);
//         }
//     }, [signedData]);

//     // Timeout for signing process
//     useEffect(() => {
//         let timeout;
//         if (isSigningInProgress) {
//             timeout = setTimeout(() => {
//                 toast.error("Signing process timed out. Please try again.");
//                 cleanupSigningState();
//                 disconnect();
//             }, 30000);
//         }

//         return () => {
//             if (timeout) {
//                 clearTimeout(timeout);
//             }
//         };
//     }, [isSigningInProgress]);

//     // Monitor signing cancellation
//     useEffect(() => {
//         if (!isSignPending && isSigningInProgress && !signatureData && !signError) {
//             setTimeout(() => {
//                 if (!signatureData && isSigningInProgress) {
//                     toast.error("Signing was cancelled or failed");
//                     cleanupSigningState();
//                     disconnect();
//                 }
//             }, 1000);
//         }
//     }, [isSignPending, isSigningInProgress, signatureData, signError]);

//     const handleWalletConnect = async (connector) => {
//         try {
//             toast.dismiss();
//             cleanupSigningState(); // Clean up before connecting
//             setShouldInitiateSigningAfterConnect(true);
//             await connect({ connector });
//         } catch (error) {
//             toast.error("Failed to connect wallet");
//             cleanupSigningState();
//         }
//     };

//     const initiateSigningProcess = () => {
//         try {
//             if (!address) {
//                 throw new Error("Wallet address not available");
//             }

//             if (!isConnected) {
//                 throw new Error("Wallet not connected");
//             }

//             if (chainId !== REQUIRED_CHAIN_ID) {
//                 throw new Error("Please switch to Etherium network");
//             }

//             const message = `${address?.toLocaleLowerCase()}weareUniform`;
//             setIsSigningInProgress(true);

//             signMessage({ message });

//         } catch (error) {
//             toast.error(error.message || "Failed to start signing process");
//             cleanupSigningState();
//             disconnect();
//         }
//     };

//     const handleLoginAPI = async (walletAddress, signature) => {
//         try {
//             if (!walletAddress || !signature) {
//                 throw new Error("Missing required authentication data");
//             }

//             const payload = {
//                 walletAddress: walletAddress?.toLocaleLowerCase(),
//                 sign: signature
//             };

//             const response = await fetch(`${ApiUrl}/auth/users/signup-signin`, {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify(payload)
//             });

//             const data = await response.json();

//             if (!response.ok) {
//                 throw new Error(data.message || `API Error: ${response.status}`);
//             }

//             setLogedIn(data.data);

//             if (data.data?.accessToken) {
//                 localStorage.setItem('accessToken', data.data.accessToken);
//             }
//             if (data.data?.refreshToken) {
//                 localStorage.setItem('refreshToken', data.data.refreshToken);
//             }

//             cleanupSigningState();
//             toast.dismiss();

//         } catch (error) {
//             toast.error(error.message || 'Authentication failed');
//             cleanupSigningState();
//             disconnect();
//         }
//     };

//     const handleWalletDisconnect = () => {
//         cleanupSigningState(); // Clean up before disconnecting
//         disconnect();
//         localStorage.removeItem('accessToken');
//         localStorage.removeItem('refreshToken');
//         previousAddressRef.current = null;
//         toast.dismiss();
//     };

//     return {
//         isConnected,
//         isSigningInProgress,
//         isSignPending,
//         isMobile,
//         connectors,
//         logedIn,
//         chainId,
//         isAutoSwitching,
//         handleWalletConnect,
//         handleWalletDisconnect,
//         switchToChain
//     };
// };